import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DashboardLayout } from "./components/DashboardLayout";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PublicForm from "./pages/PublicForm";
import CustomerLogin from "./pages/CustomerLogin";
import CustomerAuth from "./pages/CustomerAuth";
import CustomerConversations from "./pages/CustomerConversations";
import CustomerThread from "./pages/CustomerThread";
import { PaymentCancel, PaymentSuccess } from "./pages/PaymentResult";
import BusinessDashboard from "./pages/business/BusinessDashboard";
import Forms from "./pages/business/Forms";
import FormBuilder from "./pages/business/FormBuilder";
import FormFill from "./pages/business/FormFill";
import Documents from "./pages/business/Documents";
import Submissions from "./pages/business/Submissions";
import Conversations from "./pages/business/Conversations";
import BusinessSettings from "./pages/business/BusinessSettings";
import Billing from "./pages/business/Billing";
import Broadcasts from "./pages/business/Broadcasts";
import Customers from "./pages/business/Customers";
import WidgetChat from "./pages/WidgetChat";
import RatePage from "./pages/RatePage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Account from "./pages/Account";
import ConfirmPage from "./pages/ConfirmPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Businesses from "./pages/admin/Businesses";
import Templates from "./pages/admin/Templates";
import Plans from "./pages/admin/Plans";
import AdminSettings from "./pages/admin/AdminSettings";
import CmsEditor from "./pages/admin/CmsEditor";
import EmailLog from "./pages/admin/EmailLog";

function Protected({ roles, kind, ownerOnly, children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500" data-testid="loading-screen">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
  if (ownerOnly && user.staff_role === "agent") return <Navigate to="/dashboard" replace />;
  return <DashboardLayout kind={kind}>{children}</DashboardLayout>;
}

const B = (el, ownerOnly = false) => <Protected roles={["business"]} kind="business" ownerOnly={ownerOnly}>{el}</Protected>;
const A = (el) => <Protected roles={["super_admin"]} kind="admin">{el}</Protected>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/f/:formId" element={<PublicForm />} />
            <Route path="/customer/login" element={<CustomerLogin />} />
            <Route path="/customer/auth" element={<CustomerAuth />} />
            <Route path="/customer/conversations" element={<CustomerConversations />} />
            <Route path="/customer/conversations/:id" element={<CustomerThread />} />
            <Route path="/widget/:businessId" element={<WidgetChat />} />
            <Route path="/rate/:token" element={<RatePage />} />
            <Route path="/confirm/:token" element={<ConfirmPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/dashboard" element={B(<BusinessDashboard />)} />
            <Route path="/dashboard/forms" element={B(<Forms />)} />
            <Route path="/dashboard/forms/new" element={B(<FormBuilder />)} />
            <Route path="/dashboard/forms/:id/edit" element={B(<FormBuilder />)} />
            <Route path="/dashboard/forms/:id/fill" element={B(<FormFill />)} />
            <Route path="/dashboard/documents" element={B(<Documents />)} />
            <Route path="/dashboard/submissions" element={B(<Submissions />)} />
            <Route path="/dashboard/conversations" element={B(<Conversations />)} />
            <Route path="/dashboard/conversations/:id" element={B(<Conversations />)} />
            <Route path="/dashboard/settings" element={B(<BusinessSettings />, true)} />
            <Route path="/dashboard/account" element={B(<Account />)} />
            <Route path="/dashboard/customers" element={B(<Customers />)} />
            <Route path="/dashboard/broadcasts" element={B(<Broadcasts />)} />
            <Route path="/dashboard/billing" element={B(<Billing />, true)} />
            <Route path="/admin" element={A(<AdminDashboard />)} />
            <Route path="/admin/businesses" element={A(<Businesses />)} />
            <Route path="/admin/templates" element={A(<Templates />)} />
            <Route path="/admin/plans" element={A(<Plans />)} />
            <Route path="/admin/settings" element={A(<AdminSettings />)} />
            <Route path="/admin/cms" element={A(<CmsEditor />)} />
            <Route path="/admin/emails" element={A(<EmailLog />)} />
            <Route path="/admin/account" element={A(<Account />)} />
            <Route path="/dashboard/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
          </Routes>
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
